import React, { forwardRef, useContext, useState } from 'react';
import Button from '@atlaskit/button';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { GlobalContext } from 'shared-components/common/GlobalContext';
import styled from '@emotion/styled';
import { Box, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import Popup, { PopupComponentProps } from '@atlaskit/popup';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ErrorMessage = styled.div``;

const popUpContainerStyles = xcss({ padding: 'space.025', outline: 'none' });
const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(({ children, 'data-testid': testId, ...props }, ref) => (
  <Box xcss={popUpContainerStyles} testId={testId} {...props} ref={ref}>
    {children}
  </Box>
));

export const PageLoadError = ({ errorMessage, invokeKey, errorId }: { errorMessage: string | null; invokeKey: string; errorId: string }) => {
  const { t, routerOpen } = useContext(GlobalContext);
  const [isOpen, setIsOpen] = useState(false);
  const invokeKeyNullSafe = !invokeKey ? '' : invokeKey;
  const errorIdNullSafe = !errorId ? '' : errorId;

  let helpLink = 'https://codeclou.io/customfield-editor-for-jira/cloud/'; // default help link
  const buttonText = 'User Guide';
  if (errorIdNullSafe.includes('FIELDSEARCH_230928')) {
    helpLink = 'https://codeclou.io/customfield-editor-for-jira/cloud/installation/#jira-premium-allowlist';
  }

  return (
    <SectionMessage
      title={t('global.pageLoadFailErrorSectionHeadline')}
      appearance="error"
      actions={[
        <SectionMessageAction
          onClick={(e: React.MouseEvent<any>) => {
            e.preventDefault();
            routerOpen(helpLink);
          }}
        >
          <span data-e2e-macro-flag-link>{buttonText}</span>
        </SectionMessageAction>,
        <SectionMessageAction>
          <Popup
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            placement="right"
            popupComponent={CustomPopupContainer}
            content={() => (
              <Box>
                <Badge>
                  <span data-e2e-macro-flag-error-code>
                    {invokeKeyNullSafe}__{errorIdNullSafe}
                  </span>
                </Badge>
              </Box>
            )}
            trigger={(triggerProps) => (
              <Button {...triggerProps} spacing="none" appearance="link" onClick={() => setIsOpen(!isOpen)}>
                Error Key
              </Button>
            )}
          />
        </SectionMessageAction>,
      ]}
    >
      <ErrorContainer>
        <ErrorMessage>
          {errorIdNullSafe.includes('FIELDSEARCH_230928') ? (
            <>
              The app cannot reach the Jira REST API. This can have two reasons:
              <ol>
                <li style={{ marginTop: '5px' }}>
                  The app's IP adress-pool has been blocklisted by the Jira Premium Allowlist. Please allowlist the needed IP addresses as described in the <em>User Guide → Installation page</em>.
                </li>
                <li style={{ marginTop: '5px' }}>
                  The app's permission scopes were not fully accepcted during the initial installation. Please re-install the app as described in the <em>User Guide → Installation page</em>.
                </li>
              </ol>
              <div style={{ marginTop: '5px', marginBottom: '10px' }}>
                Original error message: <em>{errorMessage}</em>
              </div>
            </>
          ) : (
            <>{errorMessage}</>
          )}
        </ErrorMessage>
      </ErrorContainer>
    </SectionMessage>
  );
};
